/**
 * AdminChangePassword — İlk-giriş zorunlu parola değiştirme ekranı.
 *
 * `.env`'deki `SUPERADMIN_PASSWORD` yalnızca bootstrap (ilk-kurulum) için
 * kullanılır. Admin ilk kez giriş yaptığında backend `password_must_change=
 * True` döndürür → bu sayfa açılır. Burada parola değiştirildikten sonra
 * seed artık env parolasını re-hash etmez; DB tek kaynak olur.
 *
 * UI: AdminLogin ile aynı dot-bg + merkeze oturan form. `data-admin-scope`
 * sayesinde primary buton siyah. AdminShell tarafında guard var — admin
 * `password_must_change=True` iken diğer tüm path'ler bu sayfaya yönlenir.
 */
import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, KeyRound, ShieldCheck, Eye, EyeOff } from "lucide-react";
import { useAdminAuth } from "@/lib/admin-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/brand/Logo";
import { ADMIN_DASHBOARD_PATH, ADMIN_LOGIN_PATH } from "@/lib/admin-path";

function readError(err) {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
    return err?.message || "Parola değiştirilemedi";
}

export default function AdminChangePassword() {
    const { admin, changePassword } = useAdminAuth();
    const nav = useNavigate();

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCur, setShowCur] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    // Auth guard (standalone sayfa — AdminShell dışında).
    if (admin === null) {
        return (
            <div className="min-h-screen grid place-items-center bg-background" data-testid="admin-change-password-loading">
                <div className="h-8 w-8 rounded-full border-2 border-border border-t-foreground animate-spin" />
            </div>
        );
    }
    if (admin === false) {
        return <Navigate to={ADMIN_LOGIN_PATH} replace />;
    }

    const mustChange = Boolean(admin?.password_must_change);

    // Client-side strength hints — ultimate validation backend'de.
    const tooShort = next.length > 0 && next.length < 12;
    const sameAsCurrent = next.length > 0 && next === current;
    const mismatched = confirm.length > 0 && confirm !== next;
    const canSubmit =
        current.length > 0 &&
        next.length >= 12 &&
        next === confirm &&
        next !== current &&
        !busy;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setError("");
        try {
            await changePassword(current, next);
            nav(ADMIN_DASHBOARD_PATH, { replace: true });
        } catch (err) {
            setError(readError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-admin-scope=""
            className="min-h-screen relative bg-background text-foreground overflow-hidden"
            data-testid="admin-change-password-page"
        >
            <div className="pointer-events-none absolute inset-0 dot-bg opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
            <div className="pointer-events-none absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full blur-3xl opacity-30 bg-foreground/20" />

            <header className="relative z-10 h-16 px-6 md:px-8 flex items-center justify-between">
                <Logo />
                <span
                    className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold tracking-wide uppercase bg-foreground text-background"
                    data-testid="admin-change-password-badge"
                >
                    <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                    Süper Admin
                </span>
            </header>

            <main
                className="relative z-10 grid place-items-center px-6"
                style={{ minHeight: "calc(100vh - 64px - 48px)" }}
            >
                <div className="w-full max-w-[460px]">
                    <div className="mb-8 flex items-center justify-center">
                        <div className="h-12 w-12 rounded-xl border border-border bg-surface flex items-center justify-center">
                            <KeyRound className="h-5 w-5" strokeWidth={1.5} />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in" data-testid="admin-change-password-form">
                        <div className="text-center space-y-2">
                            <h1 className="font-display text-3xl font-bold tracking-tight">
                                {mustChange ? "Parolanı Belirle" : "Parolanı Değiştir"}
                            </h1>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {mustChange ? (
                                    <>
                                        Güvenliğin için <span className="font-medium text-foreground">ilk girişinde</span> kullandığın geçici parolayı
                                        kalıcı bir parolayla değiştirmen gerekiyor.
                                    </>
                                ) : (
                                    "Mevcut parolanı doğrulayıp yeni bir parola belirle."
                                )}
                            </p>
                        </div>

                        {admin?.email && (
                            <div className="rounded-md border border-border bg-surface/60 px-3 py-2 text-xs text-muted-foreground text-center" data-testid="admin-change-password-email">
                                Hesap: <span className="font-mono text-foreground">{admin.email}</span>
                            </div>
                        )}

                        <PasswordField
                            id="admin-cur-pw"
                            label="Mevcut Parola"
                            value={current}
                            onChange={setCurrent}
                            show={showCur}
                            onToggle={() => setShowCur((v) => !v)}
                            placeholder="Geçici parolan"
                            autoComplete="current-password"
                            autoFocus
                            disabled={busy}
                            testId="admin-change-password-current"
                        />

                        <PasswordField
                            id="admin-next-pw"
                            label="Yeni Parola"
                            value={next}
                            onChange={setNext}
                            show={showNext}
                            onToggle={() => setShowNext((v) => !v)}
                            placeholder="En az 12 karakter"
                            autoComplete="new-password"
                            disabled={busy}
                            testId="admin-change-password-next"
                            hint={
                                tooShort
                                    ? "En az 12 karakter"
                                    : sameAsCurrent
                                        ? "Yeni parola, mevcut parolayla aynı olamaz"
                                        : next.length >= 12
                                            ? "Güçlü görünüyor"
                                            : "En az 12 karakter · harf + rakam + sembol öneririz"
                            }
                            hintTone={
                                tooShort || sameAsCurrent
                                    ? "warn"
                                    : next.length >= 12
                                        ? "ok"
                                        : "muted"
                            }
                        />

                        <PasswordField
                            id="admin-confirm-pw"
                            label="Yeni Parola (Tekrar)"
                            value={confirm}
                            onChange={setConfirm}
                            show={showNext}
                            onToggle={() => setShowNext((v) => !v)}
                            placeholder="Yeni parolayı yeniden yaz"
                            autoComplete="new-password"
                            disabled={busy}
                            testId="admin-change-password-confirm"
                            hint={mismatched ? "Parolalar eşleşmiyor" : ""}
                            hintTone={mismatched ? "warn" : "muted"}
                        />

                        {error && (
                            <div
                                className="text-xs text-destructive text-center -mt-2"
                                data-testid="admin-change-password-error"
                            >
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={!canSubmit}
                            className="w-full h-11 bg-foreground text-background hover:bg-foreground/90 font-medium disabled:opacity-60"
                            data-testid="admin-change-password-submit"
                        >
                            {busy ? "Kaydediliyor…" : "Parolayı Kaydet"}
                            {!busy && <ArrowRight className="h-4 w-4 ml-1.5" strokeWidth={2} />}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground leading-relaxed">
                            Parolanı belirledikten sonra, env'deki geçici parola bir daha
                            geçerli olmayacak — DB'deki yeni parolan tek kaynak olur.
                        </p>
                    </form>
                </div>
            </main>

            <footer className="relative z-10 h-12 px-6 md:px-8 flex items-center justify-between text-[11px] text-muted-foreground">
                <span>© Sayz {new Date().getFullYear()}</span>
                <span className="font-mono">süper admin · parola kurulumu</span>
            </footer>
        </div>
    );
}

function PasswordField({
    id, label, value, onChange, show, onToggle, placeholder,
    autoComplete, autoFocus, disabled, testId, hint, hintTone,
}) {
    return (
        <div className="space-y-2">
            <label
                className="text-xs font-semibold tracking-wide uppercase text-muted-foreground"
                htmlFor={id}
            >
                {label}
            </label>
            <div className="relative">
                <Input
                    id={id}
                    type={show ? "text" : "password"}
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className="h-11 text-sm bg-surface pr-10"
                    data-testid={testId}
                    disabled={disabled}
                />
                <button
                    type="button"
                    onClick={onToggle}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                    aria-label={show ? "Parolayı gizle" : "Parolayı göster"}
                    data-testid={`${testId}-toggle`}
                    tabIndex={-1}
                >
                    {show
                        ? <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                        : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                </button>
            </div>
            {hint && (
                <p
                    className={[
                        "text-[11px] leading-snug pl-0.5",
                        hintTone === "warn"
                            ? "text-destructive"
                            : hintTone === "ok"
                                ? "text-emerald-600"
                                : "text-muted-foreground",
                    ].join(" ")}
                    data-testid={`${testId}-hint`}
                >
                    {hint}
                </p>
            )}
        </div>
    );
}
